import {remCalc} from '../../_utils'

const CARD_WIDTH = {base: remCalc(332), md: remCalc(448)}
// Only applies when slidesPerView is 'auto'; numeric slidesPerView sets an inline width that must win
const PRODUCT_SLIDE = {
    width: {base: `min(${remCalc(332)}, 85vw)`, md: remCalc(448)},
}
const NAV_BUTTON_SHADOW = `0 0 0 ${remCalc(
    1,
)} var(--chakra-colors-border-neutral-highlight, #FFF) inset, 0 ${remCalc(1)} ${remCalc(
    2,
)} 0 var(--chakra-shadows-neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 ${remCalc(
    1,
)} var(--chakra-shadows-neutral-xs, rgba(19, 24, 58, 0.08))`

const hideScrollbar = {
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    scrollbarColor: 'transparent transparent',
    '&::-webkit-scrollbar': {
        width: 0,
        height: 0,
        display: 'none',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'transparent',
    },
}

export default {
    baseStyle: {
        container: {
            width: '100%',
            maxWidth: remCalc(1440),
            margin: '0 auto',
            height: 'auto',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: ['small-1.base', 'small-1.sm', 'small-1.md', 'small-1.lg'],
            paddingLeft: ['xsmall.base', 'xsmall.sm', 'medium.md', 'medium.lg'],
            paddingRight: 0,
            paddingY: ['small-1.base', 'small-1.sm', 'medium.md', 'medium.lg'],
        },
        header: {
            flexDirection: 'column',
            gap: ['xxxsmall.base', 'xxxsmall.sm', 'xxxsmall.md', 'xxxsmall.lg'],
        },
        title: {
            textStyle: 'head-large',
            fontWeight: 'light',
            color: 'foreground.neutral.default',
            fontFamily: 'heading',
        },
        subtitle: {
            textStyle: 'head-large',
            fontWeight: 'light',
            color: 'foreground.neutral.subtle',
            fontFamily: 'heading',
        },
        tabListContainer: {
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            ...hideScrollbar,
        },
        tabListShell: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'flex-start',
            alignSelf: 'flex-start',
            width: 'max-content',
            flexShrink: 0,
            gap: 0,
            padding: ['micro.base', 'micro.sm', 'micro.md', 'micro.lg'],
            bg: 'background.base.under',
            borderRadius: 'full',
            overflowX: {base: 'auto', md: 'hidden'},
            ...hideScrollbar,
        },
        tab: {
            minH: remCalc(28),
            h: remCalc(36),
            minW: 'max-content',
            width: 'auto',
            flex: '0 0 auto',
            alignItems: 'center',
            justifyContent: 'center',
            gap: ['xxxsmall-1.base', 'xxxsmall-1.sm', 'xxxsmall-1.md', 'xxxsmall-1.lg'],
            py: 'xsmall.base',
            px: 'xsmall-1.base',
            cursor: 'pointer',
            borderRadius: 'full',
            bg: 'transparent',
            color: 'foreground.neutral.default',
            fontWeight: 'medium',
            textStyle: 'body-small',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s',

            //This is added to avoid the jump in layout when the border appears on hover
            border: 'small',
            borderStyle: 'solid',
            borderColor: 'transparent',

            _hover: {
                bg: 'background.strong.default',
                color: 'foreground.neutral.inverse',
                border: 'small',
                borderStyle: 'solid',
                borderColor: 'border.neutral.highlight',
                boxShadow: 'borderShadow',
            },
        },
        tabActive: {
            border: 'small',
            borderStyle: 'solid',
            borderColor: 'border.neutral.highlight',
            boxShadow: 'borderShadow',
            bg: 'background.strong.default',
            color: 'foreground.neutral.inverse',
            fontWeight: 'medium',
            _hover: {
                bg: 'background.strong.default',
            },
        },
        tabIcon: {
            boxSize: remCalc(14),
            color: 'currentColor',
        },
        sliderContainer: {
            position: 'relative',
            width: '100%',
            minWidth: 0,
            // Room for the card's outer box-shadow, which overflow:hidden would otherwise clip
            '.swiper': {overflow: 'hidden', paddingY: remCalc(4), marginY: remCalc(-4)},
            '.swiper-wrapper': {
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'stretch',
            },
            '.swiper-slide': {
                // Width comes from Swiper's slidesPerView; gap lives inside the slide so translate offsets stay exact
                paddingRight: {base: 'xxsmall.base', md: 'xxsmall-1.md', lg: 'xxsmall.lg'},
                boxSizing: 'border-box',
                flexShrink: 0,
                height: 'auto',
                display: 'flex',
            },
        },
        slideByVariant: {
            category: {
                width: {
                    base: `min(${remCalc(332)}, 85vw)`,
                    lg: remCalc(320),
                    xl: remCalc(334),
                    '2xl': remCalc(454),
                },
            },
            product: PRODUCT_SLIDE,
            bundle: PRODUCT_SLIDE,
            comparison: PRODUCT_SLIDE,
        },
        navButton: {
            position: 'static',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: remCalc(28),
            height: remCalc(28),
            minWidth: remCalc(28),
            borderRadius: 'full',
            color: 'foreground.neutral.default',
            background: 'background.base.default',
            boxShadow: NAV_BUTTON_SHADOW,
            _focusVisible: {
                boxShadow: `${NAV_BUTTON_SHADOW} !important`,
            },
            _active: {
                boxShadow: `${NAV_BUTTON_SHADOW} !important`,
            },
            _disabled: {
                boxShadow: `${NAV_BUTTON_SHADOW} !important`,
                opacity: 1,
            },
        },
        navButtonPrev: {
            margin: 0,
        },
        navButtonNext: {
            margin: 0,
        },
        navigationRow: {
            width: '100%',
            justifyContent: 'flex-end',
            gap: 'xxsmall.base',
            marginTop: 'xxsmall-1.base',
            px: ['medium.base', 'medium.sm', 'medium.md', 'medium.lg'],
        },
        navIcon: {
            boxSize: remCalc(16),
            color: 'foreground.neutral.default',
        },
        card: {
            width: '100%',
            height: '100%',
            minWidth: 0,
            flexDirection: 'column',
            padding: 0,
            borderRadius: 'large-1',
            overflow: 'hidden',
            border: 'none',
            boxShadow: `0 0 0 ${remCalc(
                1,
            )} var(--chakra-colors-border-neutral-highlight) inset, 0 ${remCalc(1)} ${remCalc(
                2,
            )} 0 var(--chakra-shadows-neutral-xs), 0 0 0 ${remCalc(
                1,
            )} var(--chakra-shadows-neutral-xs)`,
            background: 'background.base.default',
            textDecoration: 'none',
        },
        cardInteractive: {
            _hover: {textDecoration: 'none', background: 'background.neutral.hover'},
        },
        cardMedia: {
            width: '100%',
            flexShrink: 0,
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            borderRadius: 'small',
        },
        productMedia: {
            width: '100%',
            maxWidth: CARD_WIDTH,
            aspectRatio: '1 / 1',
            flexShrink: 0,
            '& [data-testid="product-tile-image-wrapper"]': {
                width: '100%',
                maxWidth: CARD_WIDTH,
                aspectRatio: '1 / 1',
                marginBottom: 0,
            },
        },
        productInfo: {
            boxSizing: 'border-box',
            alignItems: 'stretch',
            gap: {base: remCalc(12), lg: remCalc(16)},
            padding: remCalc(12),
        },
        productContent: {
            gap: remCalc(12),
        },
        productNameContainer: {
            width: '100%',
            minHeight: remCalc(22),
            alignItems: 'center',
        },
        productName: {
            display: 'block',
        },
        cardMediaBundle: {
            gap: ['xxsmall.base', 'xxsmall.sm', 'xxsmall.md', 'xxsmall.lg'],
        },
        cardImage: {
            width: '100%',
        },
        cardEyebrow: {
            textStyle: 'body-xsmall',
            color: 'text.neutral.secondary',
        },
        cardTitle: {
            textStyle: 'body-medium',
            fontWeight: 'medium',
        },
        characteristicsList: {
            flexDirection: 'column',
            gap: ['xxsmall.base', 'xxsmall.sm', 'xxsmall.md', 'xxsmall.lg'],
            listStyleType: 'none',
            margin: 0,
            padding: 0,
        },
        characteristicsItem: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: ['xxsmall.base', 'xxsmall.sm', 'xxsmall.md', 'xxsmall.lg'],
            paddingY: ['xxsmall-1.base', 'xxsmall-1.sm', 'xxsmall-1.md', 'xxsmall-1.lg'],
            borderBottom: 'small',
            borderStyle: 'solid',
            borderColor: 'border.neutral.subtle',
        },
        characteristicsIcon: {
            width: remCalc(14),
            height: remCalc(14),
            flexShrink: 0,
            color: 'foreground.neutral.default',
        },
        characteristicsLabel: {
            textStyle: 'body-small',
            color: 'foreground.neutral.default',
        },
        productBody: {
            gap: remCalc(6),
        },
        keyFeaturesContainer: {
            width: '100%',
            flexWrap: 'wrap',
        },
        keyFeatureText: {
            minWidth: 0,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        },
        productFooterRow: {
            width: '100%',
            minHeight: remCalc(32),
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: remCalc(8),
        },
        cardCurrentPrice: {
            textStyle: 'body-large',
            fontWeight: 'medium',
        },
        comparisonPrice: {
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: ['xxxsmall.base', 'xxxsmall.sm', 'xxxsmall.md', 'xxxsmall.lg'],
        },
        comparisonPriceValue: {
            font: 'inherit',
            whiteSpace: 'nowrap',
        },
        cardListPrice: {
            textStyle: 'body-small',
            color: 'foreground.neutral.default',
        },
        cardCta: {
            flexShrink: 0,
            alignSelf: 'center',
            marginLeft: 'auto',
        },
        categoryCard: {
            position: 'relative',
            // Grid stacks a ratio spacer and the text in one cell: card keeps the image ratio but grows with long copy
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            _before: {
                content: '""',
                gridArea: '1 / 1',
                aspectRatio: '448 / 598',
            },
            background: 'background.base.deep',
            '& [data-id="aspect-ratio-dam-image"]': {height: '100%'},
            sx: {
                '&:hover [data-testid="carousel-cards-category-sharp-image"]': {
                    transform: 'scale(1.05)',
                },
                '&:hover [data-testid="carousel-cards-category-blur-image"]': {
                    transform: 'scale(1.0605)',
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '& [data-testid="carousel-cards-category-sharp-image"], & [data-testid="carousel-cards-category-blur-image"]':
                        {transition: 'none'},
                    '&:hover [data-testid="carousel-cards-category-sharp-image"], &:hover [data-testid="carousel-cards-category-blur-image"]':
                        {transform: 'none'},
                },
            },
        },
        categoryMedia: {
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
        },
        categorySharpMedia: {
            position: 'absolute',
            inset: 0,
            transform: 'scale(1)',
            transition: 'transform 0.4s ease',
        },
        categoryBlurClip: {
            position: 'absolute',
            top: '70%',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            pointerEvents: 'none',
            overflow: 'hidden',
            _after: {
                content: '""',
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.65) 100%)',
            },
            sx: {
                maskImage:
                    'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.15) 10%, rgba(0, 0, 0, 0.6) 30%, rgba(0, 0, 0, 0.95) 50%, black 60%)',
                WebkitMaskImage:
                    'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.15) 10%, rgba(0, 0, 0, 0.6) 30%, rgba(0, 0, 0, 0.95) 50%, black 60%)',
            },
        },
        categoryBlurMedia: {
            position: 'absolute',
            top: 'calc(-100% * 70 / 30)',
            bottom: 0,
            left: 0,
            right: 0,
            filter: `blur(${remCalc(25)})`,
            transform: 'scale(1.01)',
            transition: 'transform 0.4s ease',
        },
        categoryIconAlignment: {
            position: 'absolute',
            top: 0,
            left: 0,
            padding: {base: 'xsmall.base', md: 'xsmall.md', lg: remCalc(16)},
            zIndex: 2,
            pointerEvents: 'none',
        },
        categoryIcon: {
            display: 'block',
            width: remCalc(16),
            height: remCalc(16),
            flexShrink: 0,
            objectFit: 'contain',
        },
        categoryIconContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: remCalc(32),
            height: remCalc(32),
            padding: 0,
            minWidth: remCalc(32),
            flexShrink: 0,
            lineHeight: 1,
            borderRadius: 'medium',
            background: 'background.base.default',
            color: 'foreground.neutral.default',
            boxShadow:
                '0 0 0 1px var(--border-neutral-highlight, #FFF) inset, 0 1px 2px 0 var(--neutral-xs, rgba(19, 24, 58, 0.08)), 0 0 0 1px var(--neutral-xs, rgba(19, 24, 58, 0.08))',
        },
        categoryBody: {
            position: 'relative',
            gridArea: '1 / 1',
            alignSelf: 'end',
            minWidth: 0,
            // Keeps long copy clear of the absolutely positioned icon badge
            marginTop: remCalc(64),
            padding: ['xsmall.base', 'xsmall.sm', 'xsmall.md', remCalc(16)],
            paddingTop: ['medium-2.base', 'medium-2.sm', 'medium-2.md', remCalc(16)],
            color: 'foreground.neutral.inverse',
            flexDirection: 'column',
            gap: ['xxxsmall.base', 'xxxsmall.sm', 'xxxsmall.md', remCalc(4)],
            zIndex: 1,
            overflowWrap: 'anywhere',
        },
        categoryTitle: {
            textStyle: 'body-medium',
            fontWeight: 'medium',
            color: 'foreground.neutral.inverse',
        },
        categorySub: {
            color: 'foreground.neutral.dim',
            textStyle: 'body-small',
            fontWeight: 'normal',
        },
        skeletonRow: {
            flexDirection: 'row',
            gap: remCalc(16),
            overflow: 'hidden',
        },
        skeletonCard: {
            flexShrink: 0,
            width: CARD_WIDTH,
            height: remCalc(480),
            borderRadius: 'medium',
        },
    },
}
